import { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  apiErrorStatus,
  apiErrorText,
  useGetPtoAosrRequisitesQuery,
  useGetFieldPhotosQuery,
  useGetPtoExecutiveActQuery,
  useGetPtoProjectQuery,
  useListPtoOrderRepsQuery,
  useSetFieldInspectionRequestStatusMutation,
  useSetFieldInspectionRequestDecisionMutation,
  useGenerateFieldInspectionActMutation,
  type OzhrRequisites,
} from '@shared/api';
import {
  aosrBridgePreview,
  aosrBridgePreviewText,
  aosrEvidenceNotes,
  canGenerateAct,
  fmtFieldDate,
  inspectionActState,
  inspectionDecisionLabel,
  inspectionDecisionOptions,
  inspectionTransitions,
  requestStatusLabel,
  requestStatusTone,
  type FieldInspectionRequest,
  type InspectionActState,
} from '@entities/field';
import { useAgentPermission } from '@entities/user';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';

const RowHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  /* Длинный статус («освидетельствовано») в узкой карточке — на новую строку, а не за край. */
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
`;

const RowTitle = styled.div`
  /* Базис 160px: рядом с коротким статусом заголовок тянется на всю строку, а
     длинный статус в узкой карточке уходит на следующую строку. */
  flex: 1 1 160px;
  min-width: 0;
  font-size: 14.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const RowMeta = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(2)};
  line-height: 1.55;
`;

const Flags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const InlineActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const ActForm = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Note = styled.div`
  font-size: 12.5px;
  line-height: 1.5;
  margin-top: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Warn = styled(Note)`
  color: ${({ theme }) => theme.colors.status.warning};
`;

const ErrorNote = styled.div.attrs({ role: 'alert' })`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const ActLink = styled(Link)`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

/** Сегодня в формате `<input type="date">` (локальная дата, не UTC). */
function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Бейдж состояния акта заявки. */
function actBadge(state: InspectionActState): {
  tone: 'success' | 'danger' | 'neutral';
  label: string;
} {
  switch (state) {
    case 'live':
    case 'unknown':
      return { tone: 'success', label: 'АОСР собран' };
    case 'deleted':
      return { tone: 'danger', label: 'акт удалён' };
    default:
      return { tone: 'neutral', label: 'акт не собран' };
  }
}

interface Props {
  request: FieldInspectionRequest;
  /** Подпись конструктивного элемента заявки; null — элемента нет в списке. */
  elementTitle: string | null;
  /** Элемент привязан к позиции объёма РД (числа акта возьмутся из РД, П1). */
  elementHasRd: boolean;
  /** Сколько фото привязано к заявке — они уйдут в «Приложения» акта. */
  photoCount?: number;
  /** Текущий пользователь — проверяющий ПТО (действия по заявке). */
  reviewer: boolean;
}

/**
 * Карточка заявки на освидетельствование на площадке: статус, решение и действия
 * проверяющего ПТО по автомату (draft | sent → scheduled → passed | failed;
 * failed → scheduled повторно; «пройдено» исправляется на «есть замечания» —
 * [`inspectionTransitions`]), решение по итогам и его смена, пока акт не собран
 * ([`inspectionDecisionOptions`]), сборка АОСР по пройденной заявке (№ и дата
 * акта) и пересборка, если акт удалён, переход к актам в ОСНОВА.Инженер. Перед
 * сборкой видно, что подставится в шапку акта из реквизитов АОСР проекта (а без
 * них — из реквизитов ОЖР), или предупреждение, что акт выйдет без сторон и
 * представителей (КR9, [`aosrBridgePreview`]).
 * Допустимость переходов решает сервер (409 — показываем его причину).
 */
export function InspectionRequestCard({
  request: r,
  elementTitle,
  elementHasRd,
  photoCount: providedPhotoCount,
  reviewer,
}: Props) {
  const photosQ = useGetFieldPhotosQuery(
    { siteId: r.site_id, inspection_request_id: r.id, limit: 1 },
    { skip: providedPhotoCount !== undefined },
  );
  const photoCount = providedPhotoCount ?? photosQ.currentData?.total;
  const [setStatus, { isLoading: settingStatus }] = useSetFieldInspectionRequestStatusMutation();
  const [setDecision, { isLoading: deciding }] = useSetFieldInspectionRequestDecisionMutation();
  const [generateAct, { isLoading: generating }] = useGenerateFieldInspectionActMutation();
  // Реестр актов — в ОСНОВА.Инженер: ссылку даём тем, кто его откроет.
  const ptoCanRead = useAgentPermission('pto').can_read;
  // Сервер не прислал признак удаления акта (старый бэкенд) — проверяющему
  // уточняем по карточке акта: удалённый акт не должен выглядеть «собранным».
  const probeAct =
    reviewer && ptoCanRead && !!r.executive_act_id && typeof r.act_deleted !== 'boolean';
  const actQ = useGetPtoExecutiveActQuery(r.executive_act_id ?? '', { skip: !probeAct });
  const probe = !probeAct
    ? null
    : actQ.data
      ? { deleted: !!actQ.data.act.deleted_at }
      : apiErrorStatus(actQ.error) === 404
        ? { deleted: true }
        : null;
  const actState = inspectionActState(r, probe);
  const [actNo, setActNo] = useState('');
  const [actDate, setActDate] = useState(todayIso);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // Пометки сборки о доказательной базе, подобранной по периоду заявки (КR9:
  // в текст акта они не идут — сверить перед подписанием).
  const [evidenceNotes, setEvidenceNotes] = useState<string[]>([]);

  const busy = settingStatus || deciding || generating;
  const transitions = reviewer ? inspectionTransitions(r.status) : [];
  const decisions = reviewer ? inspectionDecisionOptions(r, actState) : [];
  const canBuild = reviewer && canGenerateAct(r, actState);
  // Что мост подставит в шапку АОСР (КR9): реквизиты АОСР проекта; чего нет —
  // стороны и объект из реквизитов ОЖР (`fallback` ответа), представители — из
  // реестра приказов проекта. Читаем, только когда акт можно собрать.
  const previewOn = canBuild && ptoCanRead;
  const aosrReqQ = useGetPtoAosrRequisitesQuery(r.project_id, { skip: !previewOn });
  const registryQ = useListPtoOrderRepsQuery(r.project_id, { skip: !previewOn });
  // Сервер без `fallback` в ответе — реквизиты ОЖР берём из проекта сами.
  const needOzhr = previewOn && aosrReqQ.isSuccess && !aosrReqQ.data?.fallback;
  const projectQ = useGetPtoProjectQuery(r.project_id, { skip: !needOzhr });
  const ozhrReqs =
    aosrReqQ.data?.fallback ??
    (projectQ.data?.project.metadata as { ozhr?: OzhrRequisites } | undefined)?.ozhr;
  const preview =
    previewOn && aosrReqQ.isSuccess && !registryQ.isLoading && !projectQ.isLoading
      ? aosrBridgePreviewText(
          aosrBridgePreview(aosrReqQ.data?.requisites, ozhrReqs, registryQ.data?.items),
        )
      : null;
  const negative = r.decision === 'rejected' || r.decision === 'disputed';
  const badge = actBadge(actState);
  const actDeletedAfterBuild = r.status === 'act_generated' && actState === 'deleted';

  async function onStatus(status: string) {
    setError(null);
    setDone(null);
    try {
      await setStatus({ id: r.id, status }).unwrap();
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось изменить статус заявки.'));
    }
  }

  async function onDecision(decision: string) {
    setError(null);
    setDone(null);
    try {
      await setDecision({ id: r.id, decision }).unwrap();
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось сохранить решение по заявке.'));
    }
  }

  async function onGenerate() {
    setError(null);
    setDone(null);
    setEvidenceNotes([]);
    try {
      const res = await generateAct({
        id: r.id,
        act_no: actNo.trim() || undefined,
        act_date: actDate || undefined,
      }).unwrap();
      const no = res.act?.act_no ?? (actNo.trim() || null);
      setDone(
        res.already
          ? 'АОСР по этой заявке уже был собран.'
          : `АОСР${no ? ` № ${no}` : ''} собран — он в реестре актов ИД проекта.`,
      );
      setEvidenceNotes(aosrEvidenceNotes(res.evidence));
      setActNo('');
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось собрать АОСР.'));
    }
  }

  return (
    <Card>
      <RowHead>
        <RowTitle>{r.title}</RowTitle>
        {/* «Акт собран» у заявки, чей акт удалён, вводит в заблуждение — статусом
            показываем удаление (метка ниже его не дублирует). */}
        {actDeletedAfterBuild ? (
          <Badge $tone="danger">акт удалён</Badge>
        ) : (
          <Badge $tone={requestStatusTone(r.status)}>{requestStatusLabel(r.status)}</Badge>
        )}
      </RowHead>
      <RowMeta>
        {elementTitle && (
          <>
            Элемент: {elementTitle}
            <br />
          </>
        )}
        {(r.period_start || r.period_end) && (
          <>
            Период: {fmtFieldDate(r.period_start)} — {fmtFieldDate(r.period_end)}
            <br />
          </>
        )}
        {r.decision && (
          <>
            Решение: {inspectionDecisionLabel(r.decision)}
            {r.decided_at ? ` (${fmtFieldDate(r.decided_at)})` : ''}
            <br />
          </>
        )}
        Фото к заявке: {photoCount ?? (photosQ.isError ? 'не удалось проверить' : 'загрузка…')}
        {r.note && (
          <>
            <br />
            {r.note}
          </>
        )}
      </RowMeta>
      <Flags>
        {!actDeletedAfterBuild && <Badge $tone={badge.tone}>{badge.label}</Badge>}
        {r.needs_review && <Badge $tone="warning">нужна проверка</Badge>}
      </Flags>

      {r.status === 'draft' && (
        <Note>
          {reviewer
            ? 'Черновик прораба: в мобильном приложении шага «передать в ПТО» нет — освидетельствование назначайте сразу.'
            : 'Черновик прораба — ещё не передан в ПТО.'}
        </Note>
      )}
      {r.status === 'failed' && (
        <Note>Есть замечания — после устранения назначьте освидетельствование повторно.</Note>
      )}
      {r.status === 'passed' && negative && r.decision && (
        <Warn>
          Решение по заявке — «{inspectionDecisionLabel(r.decision)}»: АОСР по ней не собирается.
          {reviewer &&
            ' Смените решение, если замечания сняты, или отметьте «есть замечания» и назначьте освидетельствование повторно.'}
        </Warn>
      )}
      {actState === 'deleted' && (
        <Warn>
          Акт этой заявки удалён
          {r.executive_act_id ? ' (в корзине реестра актов ОСНОВА.Инженер)' : ''}.
          {reviewer &&
            (negative && r.decision
              ? ` Решение «${inspectionDecisionLabel(r.decision)}» пересборку запрещает — смените его, если замечания сняты.`
              : r.executive_act_id
                ? ' Восстановите его в реестре актов или соберите АОСР заново.'
                : ' Акт уже вычищен из корзины — соберите АОСР заново.')}
        </Warn>
      )}

      {transitions.length > 0 && (
        <InlineActions>
          {transitions.map((t) => (
            <Button
              key={t.status}
              type="button"
              $variant={t.variant}
              $size="sm"
              disabled={busy}
              title={t.title}
              onClick={() => void onStatus(t.status)}
            >
              {t.label}
            </Button>
          ))}
        </InlineActions>
      )}

      {decisions.length > 0 && (
        <InlineActions>
          <Note style={{ marginTop: 0 }}>{r.decision ? 'Сменить решение:' : 'Решение:'}</Note>
          {decisions.map((d) => (
            <Button
              key={d}
              type="button"
              $variant={d === 'accepted' ? 'secondary' : 'ghost'}
              $size="sm"
              disabled={busy}
              onClick={() => void onDecision(d)}
            >
              {inspectionDecisionLabel(d)}
            </Button>
          ))}
        </InlineActions>
      )}

      {canBuild && (
        <>
          <ActForm>
            <FieldLabel>
              № акта
              <Input
                value={actNo}
                onChange={(e) => setActNo(e.target.value)}
                placeholder="например, 12-КЖ"
                aria-label="Номер АОСР"
              />
            </FieldLabel>
            <FieldLabel>
              Дата акта
              <Input
                type="date"
                value={actDate}
                onChange={(e) => setActDate(e.target.value)}
                aria-label="Дата АОСР"
              />
            </FieldLabel>
          </ActForm>
          {!elementHasRd && (
            <Warn>
              Элемент не привязан к позиции РД — объёмы в п.1 акта будут прозой, без чисел из РД.
            </Warn>
          )}
          {photoCount === 0 && (
            <Warn>
              К заявке не привязано ни одного фото — после сборки проверьте раздел «Приложения»
              акта.
            </Warn>
          )}
          {/* Шапка будущего акта (КR9): что подставится из реквизитов проекта. */}
          {preview && (
            <>
              {preview.warning && <Warn>{preview.warning}</Warn>}
              <Note>
                {preview.summary}{' '}
                <ActLink to={`/agents/pto/${r.project_id}/acts`}>
                  {preview.warning ? 'Заполнить на «Актах ИД» →' : 'Изменить на «Актах ИД» →'}
                </ActLink>
              </Note>
            </>
          )}
          <InlineActions>
            <Button
              type="button"
              $variant="primary"
              $size="sm"
              disabled={busy}
              onClick={() => void onGenerate()}
            >
              {generating
                ? 'Собираю…'
                : actState === 'deleted'
                  ? 'Пересобрать АОСР'
                  : 'Собрать АОСР'}
            </Button>
          </InlineActions>
        </>
      )}

      {done && <Note>{done}</Note>}
      {evidenceNotes.map((n) => (
        <Warn key={n}>{n}</Warn>
      ))}
      {error && <ErrorNote>{error}</ErrorNote>}

      {r.executive_act_id && (ptoCanRead || actState !== 'deleted') && (
        <InlineActions>
          {ptoCanRead ? (
            <ActLink to={`/agents/pto/${r.project_id}/acts`}>
              {actState === 'deleted'
                ? 'Реестр актов проекта (там же корзина) →'
                : 'Открыть акты проекта в ОСНОВА.Инженер →'}
            </ActLink>
          ) : (
            <Note style={{ marginTop: 0 }}>Акт — в реестре актов ИД проекта ОСНОВА.Инженер.</Note>
          )}
        </InlineActions>
      )}
    </Card>
  );
}
